import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutInfo, testimonials, faqs, contactInfo } from '@/mocks/movies';

export default function About() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50" aria-label="品牌介绍">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>

            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">关于{aboutInfo.brandName}</h1>
              <p className="text-base md:text-lg text-primary-600 font-medium mt-3">{aboutInfo.slogan}</p>
              <p className="text-sm md:text-base text-foreground-600 mt-6 leading-relaxed">
                {aboutInfo.introduction}
              </p>
              <div className="flex items-center justify-center gap-6 mt-6 text-sm text-foreground-500 flex-wrap">
                <span>成立时间：<strong className="text-foreground-800">{aboutInfo.founded}</strong></span>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14" aria-label="核心价值观">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-2">我们的理念</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">{aboutInfo.mission}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {aboutInfo.values.map((val, index) => (
                <div key={index} className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-4">
                    <i className={`text-xl ${
                      index === 0 ? 'ri-movie-line' :
                      index === 1 ? 'ri-user-heart-line' :
                      index === 2 ? 'ri-gift-line' :
                      'ri-rocket-line'
                    }`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{val.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{val.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50" aria-label="用户口碑">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">"{t.content}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-background-200">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-sm font-medium text-foreground-800">{t.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14" aria-label="常见问题">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">常见问题</h2>
            <div className="flex flex-col gap-3">
              {faqs.map((faq) => (
                <details key={faq.id} className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden cursor-pointer">
                  <summary className="px-5 md:px-6 py-4 flex items-center justify-between text-sm md:text-base font-medium text-foreground-800 list-none">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 md:px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50" aria-label="联系方式">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">联系我们</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 max-w-4xl mx-auto">
              <div className="text-center p-5">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-3">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h3>
                <p className="text-sm text-foreground-500">{contactInfo.email}</p>
              </div>
              <div className="text-center p-5">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-3">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-1">客服电话</h3>
                <p className="text-sm text-foreground-500">{contactInfo.phone}</p>
              </div>
              <div className="text-center p-5">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mx-auto mb-3">
                  <i className="ri-wechat-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-1">微信公众号</h3>
                <p className="text-sm text-foreground-500">{contactInfo.wechat}</p>
              </div>
              <div className="text-center p-5">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-3">
                  <i className="ri-map-pin-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-1">公司地址</h3>
                <p className="text-sm text-foreground-500">{contactInfo.address}</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}